import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { formatINR, formatDateTime } from "../lib/format";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ImageIcon } from "lucide-react";

function CollectionCard({ c, onApprove, onReject, isAdmin }) {
    const statusTone =
        c.status === "pending"
            ? "bg-amber-50 text-amber-700 border-amber-100"
            : c.status === "approved"
              ? "bg-emerald-50 text-emerald-700 border-emerald-100"
              : "bg-rose-50 text-rose-700 border-rose-100";
    return (
        <Card className="rounded-2xl border-slate-100 p-4 sm:p-5" data-testid={`collection-card-${c.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-slate-900">
                            {c.customer_name || "—"}
                        </div>
                        <span className="text-[10px] font-mono uppercase bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                            {c.customer_code}
                        </span>
                        <Badge className={`${statusTone} hover:${statusTone}`}>{c.status}</Badge>
                        <span className="text-[10px] uppercase tracking-wider bg-blue-50 text-blue-900 px-1.5 py-0.5 rounded">
                            {c.collection_type}
                        </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                        {formatDateTime(c.collection_datetime || c.submitted_at)}{c.backdated ? " · BACKDATED" : ""} · by {c.collected_by_name || c.submitted_by_name || c.submitted_by} ·{" "}
                        <span className="uppercase">{c.mode}</span>
                    </div>
                    {c.notes && <div className="text-sm text-slate-700 mt-1.5">{c.notes}</div>}
                    {c.rejection_reason && (
                        <div className="text-xs text-rose-700 mt-1.5">
                            ✕ {c.rejection_reason}
                        </div>
                    )}
                </div>
                <div className="text-right">
                    <div className="num text-xl font-bold text-slate-900">{formatINR(c.amount)}</div>
                </div>
            </div>

            {c.proof && (
                <a
                    href={c.proof}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 text-xs text-blue-900 font-semibold"
                >
                    <ImageIcon className="h-3.5 w-3.5" /> View payment proof
                </a>
            )}

            {isAdmin && c.status === "pending" && (
                <div className="mt-3 flex gap-2">
                    <Button
                        size="sm"
                        onClick={() => onApprove(c.id)}
                        data-testid={`approve-${c.id}`}
                        className="h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white tap"
                    >
                        <CheckCircle2 className="h-4 w-4 mr-1" />
                        Approve
                    </Button>
                    <Dialog>
                        <DialogTrigger asChild>
                            <Button
                                size="sm"
                                variant="outline"
                                data-testid={`reject-${c.id}`}
                                className="h-9 rounded-lg border-rose-200 text-rose-700 hover:bg-rose-50"
                            >
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject
                            </Button>
                        </DialogTrigger>
                        <RejectDialog id={c.id} onReject={onReject} />
                    </Dialog>
                </div>
            )}
        </Card>
    );
}

function RejectDialog({ id, onReject }) {
    const [reason, setReason] = useState("");
    return (
        <DialogContent className="rounded-2xl">
            <DialogHeader>
                <DialogTitle>Reject Collection</DialogTitle>
            </DialogHeader>
            <Textarea
                placeholder="Reason for rejection…"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="rounded-xl"
                data-testid="reject-reason-input"
            />
            <DialogFooter>
                <Button
                    onClick={() => onReject(id, reason)}
                    data-testid="confirm-reject-btn"
                    className="bg-rose-600 hover:bg-rose-700"
                >
                    Reject
                </Button>
            </DialogFooter>
        </DialogContent>
    );
}

export default function Collections() {
    const { t } = useLang();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");

    async function load() {
        const { data } = await api.get("/collections");
        setItems(data);
    }
    useEffect(() => {
        load();
    }, []);

    async function approve(id) {
        try {
            await api.post(`/collections/${id}/approve`);
            toast.success("Approved");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }
    async function reject(id, reason) {
        if (!reason.trim()) return toast.error("Add a reason");
        try {
            await api.post(`/collections/${id}/reject`, { reason });
            toast.success("Rejected");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    const filter = (status) =>
        items
            .filter((i) => i.status === status)
            .filter(
                (i) =>
                    !q ||
                    (i.customer_name || "").toLowerCase().includes(q.toLowerCase()) ||
                    (i.customer_mobile || "").includes(q)
            );

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
                    {isAdmin ? t("approvals") : t("collections")}
                </h1>
            </div>

            <Input
                placeholder="Search by customer name or mobile"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="h-11 rounded-xl bg-white"
                data-testid="collections-search"
            />

            <Tabs defaultValue="pending">
                <TabsList className="bg-white border border-slate-100 rounded-xl">
                    <TabsTrigger value="pending" data-testid="tab-pending">
                        {t("pending")} ({filter("pending").length})
                    </TabsTrigger>
                    <TabsTrigger value="approved" data-testid="tab-approved">
                        {t("approved")} ({filter("approved").length})
                    </TabsTrigger>
                    <TabsTrigger value="rejected" data-testid="tab-rejected">
                        {t("rejected")} ({filter("rejected").length})
                    </TabsTrigger>
                </TabsList>
                {["pending", "approved", "rejected"].map((st) => (
                    <TabsContent value={st} key={st} className="mt-4 space-y-3">
                        {filter(st).length === 0 ? (
                            <Card className="rounded-2xl border-slate-100 p-8 text-center text-sm text-slate-400">
                                {t("no_data")}
                            </Card>
                        ) : (
                            filter(st).map((c) => (
                                <CollectionCard
                                    key={c.id}
                                    c={c}
                                    isAdmin={isAdmin}
                                    onApprove={approve}
                                    onReject={reject}
                                />
                            ))
                        )}
                    </TabsContent>
                ))}
            </Tabs>
        </div>
    );
}
